import { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

interface MssvBarcodeProps {
	mssv: string;
	className?: string;
}

/**
 * Mã vạch CODE128 của MSSV, để máy quét đọc thẳng từ màn hình hoặc từ ảnh thẻ.
 *
 * Vẽ vào `<svg>` chứ không phải `<canvas>`: thẻ này được chụp lại bằng
 * `dom-to-image` / `modern-screenshot` / `html2canvas`, và canvas hay ra ô trắng
 * trong ảnh xuất, còn SVG thì cả ba thư viện đều dựng đúng.
 *
 * Không hiện dãy số dưới mã vạch — MSSV đã nằm ngay cạnh ở tiêu đề thẻ, in lại
 * lần nữa chỉ tốn chỗ.
 */
export default function MssvBarcode({ mssv, className }: MssvBarcodeProps) {
	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		const svg = svgRef.current;
		if (!svg || !mssv) return;

		try {
			JsBarcode(svg, mssv, {
				format: "CODE128",
				// `width` là bề rộng một module. Bản in thu nhỏ thẻ còn `zoom: 0.79`
				// (xem @media print trong index.css), nên 2px thành ~1.6px ≈ 0.42mm —
				// vẫn trên ngưỡng ~0.25mm mà CODE128 cần để máy quét đọc được.
				width: 2,
				height: 40,
				displayValue: false,
				margin: 0,
				// Đen thuần, không phải màu thương hiệu: máy quét cần tương phản cao,
				// và máy in mặc định lược bớt màu.
				lineColor: "#000000",
				background: "#ffffff",
			});
		} catch {
			// Một MSSV có ký tự lạ làm JsBarcode ném lỗi. Thẻ sinh viên là màn hình
			// chính của quầy tiếp nhận, nên mất mã vạch phải chỉ là thiếu một chi
			// tiết, không được kéo sập cả thẻ — dọn SVG rồi thôi.
			while (svg.firstChild) svg.removeChild(svg.firstChild);
		}
	}, [mssv]);

	if (!mssv) return null;

	return (
		<svg
			ref={svgRef}
			className={className}
			aria-label={`Mã vạch MSSV ${mssv}`}
			// Chặn trình duyệt/máy in lược bớt màu — mã vạch mất tương phản là mã vạch
			// không quét được.
			style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
		/>
	);
}
